import { useRef, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "./ui/alert-dialog";

const METRICS = [
  ["existing_accounts", "Existing accounts"],
  ["legacy_accounts", "Legacy accounts to migrate"],
  ["new_registration_accounts", "New registration accounts"],
  ["legacy_remaining_minutes", "Legacy remaining minutes"],
  ["pending_historical_orders", "Pending historical orders"],
];

const MigrationReport = ({ report }) => <div data-testid="pricing-migration-report" className="space-y-4">
  <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono text-zinc-400">
    <span data-testid="pricing-migration-mode">{report.mode === "apply" ? "Apply report" : "Read-only dry-run"}</span>
    <span data-testid="pricing-migration-version">{report.version}</span>
    <span data-testid="pricing-migration-approval-status">Cutover approved at preflight: {report.cutover_approved ? "Yes" : "No"}</span>
  </div>
  <p className="text-xs text-zinc-400" data-testid="pricing-migration-cutover-label">Reviewed cutover (ISO-8601):
    <span data-testid="pricing-migration-cutover-timestamp" className="block font-mono text-zinc-200 mt-1 break-all">{report.cutover}</span>
  </p>
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
    {METRICS.map(([key, label]) => <div key={key} className="bg-zinc-950 p-3" data-testid={`pricing-migration-${key.replaceAll("_", "-")}`}>
      <div className="text-xs text-zinc-400">{label}</div>
      <div className="font-mono text-lg text-white mt-1">{report[key].toLocaleString(undefined, { maximumFractionDigits: 6 })}</div>
    </div>)}
    <div className="bg-zinc-950 p-3" data-testid="pricing-migration-error-count"><div className="text-xs text-zinc-400">Errors</div><div className={`font-mono text-lg mt-1 ${report.errors.length ? "text-amber-300" : "text-white"}`}>{report.errors.length}</div></div>
  </div>
  <p data-testid="pricing-migration-report-scope" className="text-xs text-zinc-500">Account counts and minutes describe the preflight snapshot, not accounts successfully applied. Historical orders are counted only; no payment is created or reconciled.</p>
  {!!report.errors.length && <div data-testid="pricing-migration-errors" role="alert" className="border border-amber-500/30 p-3 text-xs text-amber-300 max-h-52 overflow-auto space-y-2">
    <p>{report.mode === "apply" ? "Apply completed with errors; some accounts may have been processed. Review these errors, then run a new dry-run." : "Resolve these preflight errors and run a new dry-run before applying."}</p>
    {report.errors.map((item, i) => <p data-testid={`pricing-migration-error-${i}`} key={`${item.user_id}-${i}`} className="break-words"><span className="font-mono">{item.user_id}</span>: {item.error}</p>)}
  </div>}
  <details className="text-xs text-zinc-400" data-testid="pricing-migration-report-json">
    <summary data-testid="pricing-migration-report-json-toggle" className="cursor-pointer hover:text-white transition-colors">Full migration report (JSON)</summary>
    <pre data-testid="pricing-migration-report-json-content" className="mt-3 p-3 bg-zinc-950 border border-zinc-800 overflow-auto max-h-72">{JSON.stringify(report, null, 2)}</pre>
  </details>
</div>;

const ApplyConfirmation = ({ open, onOpenChange, report, confirmed, setConfirmed, onApply, busy }) => (
  <AlertDialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
    <AlertDialogContent data-testid="pricing-migration-confirm-dialog" className="bg-zinc-950 border-zinc-700 text-white rounded-none max-w-[min(94vw,520px)]">
      <AlertDialogHeader>
        <AlertDialogTitle>Approve and apply pricing migration?</AlertDialogTitle>
        <AlertDialogDescription className="text-zinc-400">
          This writes billing accounts and grants in this backend's database. Drain processing jobs first. The existing approval gate and immutable cutover checks still apply. No Cashfree calls are made.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <p data-testid="pricing-migration-confirm-cutover" className="font-mono text-xs text-amber-300 break-all">Cutover: {report?.cutover}</p>
      <label htmlFor="pricing-migration-approval" className="flex items-start gap-3 text-sm text-zinc-300">
        <Checkbox id="pricing-migration-approval" data-testid="pricing-migration-approval-checkbox" checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} disabled={busy} className="mt-0.5 border-zinc-500" />
        <span>I reviewed this dry-run and explicitly approve applying its cutover. My authenticated admin identity will be recorded as the operator when approving the cutover.</span>
      </label>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="pricing-migration-cancel-btn" disabled={busy} className="bg-transparent border-zinc-700 rounded-none hover:bg-zinc-800 hover:text-white">Cancel</AlertDialogCancel>
        <Button data-testid="pricing-migration-confirm-apply-btn" onClick={onApply} disabled={!confirmed || busy} className="bg-amber-600 hover:bg-amber-500 text-white rounded-none">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{busy ? "Applying…" : "Approve and apply"}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const PricingMigrationAdmin = () => {
  const [report, setReport] = useState(null);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [canApply, setCanApply] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const inFlight = useRef(false);

  const run = async (mode) => {
    if (inFlight.current || (mode === "apply" && (!canApply || !confirmed))) return;
    inFlight.current = true;
    setPending(mode); setError(""); setStatus(""); setCanApply(false);
    const body = mode === "apply"
      ? { cutover: report.cutover, approve_cutover: true }
      : {};
    if (mode === "dry-run") setReport(null);
    try {
      const { data } = await api.post(`/admin/pricing-migration/${mode}`, body);
      setReport(data);
      setCanApply(mode === "dry-run" && data.errors.length === 0);
      const message = data.errors.length
        ? `${mode === "apply" ? "Apply" : "Dry-run"} returned errors. Review the report.`
        : mode === "apply" ? "Apply completed. Run a new dry-run to verify account counts." : "Read-only dry-run complete. Review the report before applying.";
      setStatus(message);
      if (data.errors.length) toast.warning(message); else toast.success(message);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.message || "Migration request failed. Run a new dry-run before retrying; if apply was requested, its outcome may be incomplete.";
      setError(message); toast.error(message);
    } finally {
      setPending(null); setConfirmed(false); setConfirmOpen(false); inFlight.current = false;
    }
  };

  return <section data-testid="admin-pricing-migration-section" className="border border-zinc-800 bg-zinc-900/40 p-5 md:p-6 space-y-5">
    <header><h2 className="text-lg font-heading font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" />Pricing migration</h2>
      <p data-testid="pricing-migration-runtime-note" className="mt-2 text-sm text-zinc-400">Runs inside this backend using its configured MONGO_URL / DB_NAME. On the deployed app, this uses the deployed database—not the preview database. Connection values are never exposed here.</p>
      <p data-testid="pricing-migration-readonly-note" className="mt-2 text-xs text-zinc-500">Dry-run is read-only: no account, cutover or index writes, and no Cashfree calls. Nothing runs until you click a button.</p>
    </header>
    <div className="flex flex-wrap gap-3">
      <Button data-testid="pricing-migration-dry-run-btn" disabled={!!pending} onClick={() => run("dry-run")} variant="outline" className="bg-zinc-950 border-zinc-700 text-zinc-200 hover:bg-zinc-800 hover:text-white rounded-none">
        {pending === "dry-run" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{pending === "dry-run" ? "Running dry-run…" : "Run read-only dry-run"}
      </Button>
      <Button data-testid="pricing-migration-apply-btn" disabled={!canApply || !!pending} onClick={() => { setConfirmed(false); setConfirmOpen(true); }} className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-none">Review and apply</Button>
    </div>
    {pending && <p data-testid="pricing-migration-progress" role="status" className="text-xs text-zinc-400">{pending === "apply" ? "Applying the reviewed migration. Please keep this page open; do not submit again." : "Reading account and historical-order data…"}</p>}
    {error && <p data-testid="pricing-migration-error-banner" role="alert" className="text-sm text-red-300 border-l-2 border-red-500 pl-3">{error}</p>}
    {status && <p data-testid="pricing-migration-status" role="status" className="text-xs text-zinc-300">{status}</p>}
    {report && <MigrationReport report={report} />}
    <ApplyConfirmation open={confirmOpen} onOpenChange={setConfirmOpen} report={report} confirmed={confirmed} setConfirmed={setConfirmed} onApply={() => run("apply")} busy={pending === "apply"} />
  </section>;
};
